import THREE from "three";

function translateParams(params) {
  if (!params) return params;
  const out = { ...params };
  if ('vertexColors' in out) {out.vertexColors = out.vertexColors ? THREE.VertexColors : THREE.NoColors;}
  return out;
}

/**
 * A simple, unlit material for 3D surfaces.
 *
 * @alias Autodesk.Viewing.Scene.UnlitMaterial
 * @memberOf Autodesk.Viewing.Scene
 * @class
 */
export class UnlitMaterial extends THREE.MeshBasicMaterial {
  /**
   * @alias Autodesk.Viewing.Scene.UnlitMaterial
   * @param {object}                          [params]
   * @param {Autodesk.Viewing.Scene.Color}    [params.color]         Diffuse surface color. Default: white.
   * @param {number}                          [params.opacity]       Surface opacity in `[0, 1]`. Default: `1`.
   * @param {Autodesk.Viewing.Scene.Texture}  [params.map]           Color (albedo) texture.
   * @param {Autodesk.Viewing.Scene.Texture}  [params.alphaMap]      Alpha mask texture.
   * @param {boolean}                         [params.vertexColors]  Multiply surface color by per-vertex colors. Default: `false`.
   * @param {Autodesk.Viewing.Scene.Side}     [params.side]          Faces to render. Default: `Side.Front`.
   * @param {boolean}                         [params.depthWrite]    Write to the depth buffer. Default: `true`.
   * @param {boolean}                         [params.depthTest]     Depth-test against the depth buffer. Default: `true`.
   * @param {boolean}                         [params.transparent]   Render in the transparent pass with alpha blending. Default: `false`.
   */
  constructor(params) {
    super(translateParams(params));

    // Opt out of MaterialManager's automatic env-map attachment, since this is an unlit material that shouldn't be affected by environment maps.
    this.disableEnvMap = true;
  }

  /**
   * @alias Autodesk.Viewing.Scene.UnlitMaterial#getColor
   * @returns {Autodesk.Viewing.Scene.Color} A defensive clone of the diffuse color.
   */
  getColor() {return this.color.clone();}

  /**
   * @alias Autodesk.Viewing.Scene.UnlitMaterial#setColor
   * @param {Autodesk.Viewing.Scene.Color} c Diffuse color. Copied into the existing Color.
   */
  setColor(c) {
    this.color.copy(c);
    this.uniformsNeedUpdate = true;
  }

  /**
   * @alias Autodesk.Viewing.Scene.UnlitMaterial#getOpacity
   * @returns {number} Surface opacity in `[0, 1]`.
   */
  getOpacity() {return this.opacity;}

  /**
   * @alias Autodesk.Viewing.Scene.UnlitMaterial#setOpacity
   * @param {number} o Surface opacity in `[0, 1]`. Requires `setTransparent(true)` for `< 1`.
   */
  setOpacity(o) {
    this.opacity = o;
    this.uniformsNeedUpdate = true;
  }

  /**
   * Returns the color (albedo) texture.
   * @alias Autodesk.Viewing.Scene.UnlitMaterial#getMap
   * @returns {?Autodesk.Viewing.Scene.Texture} Color (albedo) texture, or `null`.
   */
  getMap() {return this.map;}

  /**
   * Sets the color (albedo) texture.
   * @alias Autodesk.Viewing.Scene.UnlitMaterial#setMap
   * @param {?Autodesk.Viewing.Scene.Texture} t Color (albedo) texture, or `null`.
   */
  setMap(t) {
    this.map = t;
    this.needsUpdate = true;
  }

  /**
   * Gets the alpha mask texture.
   * @alias Autodesk.Viewing.Scene.UnlitMaterial#getAlphaMap
   * @returns {?Autodesk.Viewing.Scene.Texture} Alpha mask texture, or `null`.
   */
  getAlphaMap() {return this.alphaMap;}

  /**
   * Sets the alpha mask texture.
   * @alias Autodesk.Viewing.Scene.UnlitMaterial#setAlphaMap
   * @param {?Autodesk.Viewing.Scene.Texture} t Alpha mask texture, or `null`.
   */
  setAlphaMap(t) {
    this.alphaMap = t;
    this.needsUpdate = true;
  }

  /**
   * @alias Autodesk.Viewing.Scene.UnlitMaterial#getVertexColors
   * @returns {boolean} Whether to multiply the surface color by per-vertex colors.
   */
  getVertexColors() {return this.vertexColors !== THREE.NoColors;}

  /**
   * @alias Autodesk.Viewing.Scene.UnlitMaterial#setVertexColors
   * @param {boolean} v Whether to multiply the surface color by per-vertex colors.
   */
  setVertexColors(v) {
    this.vertexColors = v ? THREE.VertexColors : THREE.NoColors;
    this.needsUpdate = true;
  }

  /**
   * Gets which face(s) of a polygon are rendered.
   * @alias Autodesk.Viewing.Scene.UnlitMaterial#getSide
   * @returns {Autodesk.Viewing.Scene.Side} Faces to render.
   */
  getSide() {return this.side;}

  /**
   * Sets which face(s) of a polygon are rendered.
   * @alias Autodesk.Viewing.Scene.UnlitMaterial#setSide
   * @param {Autodesk.Viewing.Scene.Side} s Faces to render.
   */
  setSide(s) {
    this.side = s;
    this.needsUpdate = true;
  }

  /**
   * @alias Autodesk.Viewing.Scene.UnlitMaterial#getDepthWrite
   * @returns {boolean} Whether to write to the depth buffer.
   */
  getDepthWrite() {return this.depthWrite;}

  /**
   * @alias Autodesk.Viewing.Scene.UnlitMaterial#setDepthWrite
   * @param {boolean} v Whether to write to the depth buffer.
   */
  setDepthWrite(v) {
    this.depthWrite = v;
    this.needsUpdate = true;
  }

  /**
   * @alias Autodesk.Viewing.Scene.UnlitMaterial#getDepthTest
   * @returns {boolean} Whether to depth-test against the depth buffer.
   */
  getDepthTest() {return this.depthTest;}

  /**
   * @alias Autodesk.Viewing.Scene.UnlitMaterial#setDepthTest
   * @param {boolean} v Whether to depth-test against the depth buffer.
   */
  setDepthTest(v) {
    this.depthTest = v;
    this.needsUpdate = true;
  }

  /**
   * @alias Autodesk.Viewing.Scene.UnlitMaterial#getTransparent
   * @returns {boolean} Whether the material renders in the transparent pass.
   */
  getTransparent() {return this.transparent;}

  /**
   * @alias Autodesk.Viewing.Scene.UnlitMaterial#setTransparent
   * @param {boolean} v Whether the material renders in the transparent pass with alpha blending.
   */
  setTransparent(v) {
    this.transparent = v;
    this.needsUpdate = true;
  }
}